/**
 * Serviço de Push Notifications
 * Gerencia inscrições e permissões do navegador
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// VAPID Public Key - gerada para o projeto
// Em produção, gerar uma nova chave e armazenar no .env
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

/**
 * Converte a chave VAPID para formato Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Verifica se o navegador suporta Push Notifications
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Verifica o status atual da permissão
 */
export function getPermissionStatus() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', 'default'
}

/**
 * Registra o Service Worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker não suportado');
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push] Service Worker registrado:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[Push] Erro ao registrar Service Worker:', error);
    throw error;
  }
}

/**
 * Solicita permissão para notificações
 */
export async function requestPermission() {
  if (!isPushSupported()) {
    throw new Error('Push Notifications não suportadas neste navegador');
  }

  const permission = await Notification.requestPermission();
  console.log('[Push] Permissão:', permission);
  return permission;
}

/**
 * Inscreve o usuário para receber push notifications
 */
export async function subscribeToPush(token) {
  if (!isPushSupported()) {
    throw new Error('Push Notifications não suportadas');
  }

  // Verificar permissão
  if (Notification.permission !== 'granted') {
    const permission = await requestPermission();
    if (permission !== 'granted') {
      throw new Error('Permissão negada');
    }
  }

  // Registrar Service Worker
  const registration = await registerServiceWorker();

  // Aguardar o SW estar pronto
  await navigator.serviceWorker.ready;

  // Criar inscrição push
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  });

  console.log('[Push] Inscrito:', subscription);

  // Enviar inscrição para o backend
  const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      subscription: subscription.toJSON()
    })
  });

  if (!response.ok) {
    throw new Error('Erro ao salvar inscrição no servidor');
  }

  return subscription;
}

/**
 * Cancela a inscrição de push notifications
 */
export async function unsubscribeFromPush(token) {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await subscription.unsubscribe();

    // Notificar o backend
    await fetch(`${API_URL}/api/notifications/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    });
  }

  return true;
}

/**
 * Verifica se o usuário já está inscrito
 */
export async function isSubscribed() {
  if (!isPushSupported()) return false;

  try {
    // Adicionar timeout para não travar
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Timeout')), 3000)
    );
    
    const readyPromise = navigator.serviceWorker.ready;
    const registration = await Promise.race([readyPromise, timeoutPromise]);
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Busca as preferências de notificação do usuário
 */
export async function getNotificationPreferences(token) {
  const response = await fetch(`${API_URL}/api/notifications/preferences`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Erro ao buscar preferências');
  }

  return response.json();
}

/**
 * Salva as preferências de notificação do usuário
 */
export async function saveNotificationPreferences(token, preferences) {
  const response = await fetch(`${API_URL}/api/notifications/preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(preferences)
  });

  if (!response.ok) {
    throw new Error('Erro ao salvar preferências');
  }

  return response.json();
}

/**
 * Envia uma notificação de teste
 */
export async function sendTestNotification(token) {
  const response = await fetch(`${API_URL}/api/notifications/test`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error('Erro ao enviar notificação de teste');
  }

  return response.json();
}
